import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Copy, Eye, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface InfluencerInvite {
  code: string;
  influencer_email: string | null;
  influencer_name: string | null;
  created_at: string;
  expire_at: string;
  status: string;
  current_uses: number;
  used_by_email: string | null;
}

const InfluencerInviteManager = () => {
  const [invites, setInvites] = useState<InfluencerInvite[]>([]);
  const [isLoadingInvites, setIsLoadingInvites] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchInfluencerInvites();
  }, []);

  const fetchInfluencerInvites = async () => {
    try {
      // Get influencer invites directly from database
      const { data: invites, error } = await supabase
        .from('user_invites')
        .select(`
          invite_code,
          email_hint,
          created_at,
          expires_at,
          status,
          uses_count,
          metadata,
          invited_user_id
        `)
        .eq('metadata->>type', 'influencer')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform data to match expected format
      const transformedInvites = (invites || []).map(invite => {
        const metadata = invite.metadata as any;
        return {
          code: invite.invite_code,
          influencer_email: invite.email_hint,
          influencer_name: metadata?.influencer_name || null,
          created_at: invite.created_at,
          expire_at: invite.expires_at,
          status: invite.status,
          current_uses: invite.uses_count,
          used_by_email: null // We'll skip this for simplicity
        };
      });
      
      setInvites(transformedInvites);
    } catch (error) {
      console.error('Error fetching influencer invites:', error);
      toast({
        title: "Error",
        description: "Failed to fetch influencer invites",
        variant: "destructive"
      });
    } finally {
      setIsLoadingInvites(false);
    }
  };

  const openSupabaseEditor = () => {
    window.open('https://supabase.com/dashboard/project/euqswbxgwcbpmdnetphs/sql/new', '_blank');
  };

  const copyInviteLink = async (code: string) => {
    const inviteUrl = `${window.location.origin}/auth?register=true&invite=${code}`;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast({
        title: "Copied!",
        description: "Invite link copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy invite link",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string, currentUses: number) => {
    if (currentUses > 0) {
      return <Badge className="bg-green-100 text-green-800">Used</Badge>;
    }
    if (status === 'active') {
      return <Badge className="bg-blue-100 text-blue-800">Active</Badge>;
    }
    return <Badge variant="secondary">{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Influencer Invite Manager</h2>
          <p className="text-muted-foreground">Manage special invite codes for influencers via Supabase SQL Editor</p>
        </div>

        <Button onClick={openSupabaseEditor} className="gap-2">
          <Database className="h-4 w-4" />
          Open SQL Editor
        </Button>
      </div>

      <Card className="border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="text-blue-800">Generate Influencer Invites via SQL</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="prose max-w-none">
            <p className="text-blue-700 mb-4">
              Use this SQL query in the Supabase SQL Editor to generate influencer invite codes:
            </p>
            <pre className="bg-white p-4 rounded border text-sm overflow-x-auto">
{`-- Generate an influencer invite code
-- Replace the values below with actual influencer details

INSERT INTO user_invites (
  invite_code,
  inviter_id,
  email_hint,
  max_uses,
  uses_count,
  expires_at,
  status,
  metadata
) VALUES (
  'INF_' || UPPER(SUBSTRING(gen_random_uuid()::text, 1, 8)), -- Auto-generate code
  (SELECT id FROM auth.users WHERE email = 'your-admin@email.com'), -- Your admin email
  'influencer@example.com', -- Influencer email (optional)
  1, -- Max uses
  0, -- Current uses
  NOW() + INTERVAL '30 days', -- Expires in 30 days
  'pending', -- Status
  jsonb_build_object(
    'type', 'influencer',
    'influencer_name', 'Influencer Name',
    'special_allowances', 10, -- Number of invites they'll get
    'generated_by_admin', true
  )
) RETURNING invite_code;`}
            </pre>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Active Influencer Invites
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingInvites ? (
            <div className="text-center py-8">Loading invites...</div>
          ) : invites.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No influencer invites generated yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Influencer</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Used By</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.map((invite) => (
                    <TableRow key={invite.code}>
                      <TableCell className="font-mono text-sm">
                        {invite.code}
                      </TableCell>
                      <TableCell>{invite.influencer_name || '-'}</TableCell>
                      <TableCell>{invite.influencer_email || '-'}</TableCell>
                      <TableCell>
                        {getStatusBadge(invite.status, invite.current_uses)}
                      </TableCell>
                      <TableCell>{formatDate(invite.created_at)}</TableCell>
                      <TableCell>{invite.used_by_email || '-'}</TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyInviteLink(invite.code)}
                          className="gap-1"
                        >
                          <Copy className="h-3 w-3" />
                          Copy Link
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InfluencerInviteManager;